import { Upload, message } from 'antd';
import { FileOutlined } from '@ant-design/icons';
import { useIntl } from 'react-intl';
import { UploadFile, UploadProps } from 'antd';
import { StyledFileUploader } from './style';
import { CustomUploadList } from './Uploader';
import { Label } from 'ui/Label';
import { options } from '../../config/config';

interface ExtendedUploadFile extends UploadFile {
  file_path?: string;
  isExisting?: boolean;
  file_size?: number;
  file_format?: string;
  created_at?: string;
}

interface FileUploaderProps {
  name: string;
  label?: string;
  fileList: UploadFile[];
  onFileListChange: (fileList: UploadFile[]) => void;
  acceptedTypes?: string[];
  maxFiles?: number;
  maxSize?: number; // in MB
  multiple?: boolean;
  disabled?: boolean;
  onDownload?: (file: UploadFile) => void;
  onDelete?: (file: UploadFile) => void;
  existingFilePaths?: string[];
  onFileUploadSuccess?: (filePath: string) => void;
}

export const FileUploader = ({
  name,
  label,
  fileList,
  onFileListChange,
  acceptedTypes = options.documents.allowedFormats,
  maxFiles = options.documents.maxFiles,
  maxSize = options.documents.maxSizeInMB,
  multiple = false,
  disabled = false,
  onDownload,
  onDelete,
  existingFilePaths = [],
  onFileUploadSuccess,
}: FileUploaderProps) => {
  const intl = useIntl();

  const createUploadFileFromPath = (filePath: string): ExtendedUploadFile => {
    const fileName = filePath.split('/').pop() || 'Unknown file';
    const fileFormat = fileName.includes('.')
      ? fileName.split('.').pop()?.toUpperCase()
      : undefined;
    return {
      uid: `existing-${filePath}`,
      name: fileName,
      status: 'done' as const,
      file_path: filePath,
      isExisting: true,
      file_format: fileFormat,
    };
  };

  const allFiles = [
    ...existingFilePaths.map(createUploadFileFromPath),
    ...fileList,
  ];

  const isMaxFilesReached = Boolean(maxFiles && allFiles.length >= maxFiles);
  const effectiveDisabled = disabled || isMaxFilesReached;

  const validateFile = (file: File): boolean => {
    if (isMaxFilesReached) {
      message.warning(
        `${intl.formatMessage({ id: 'file_uploader.max_files' })} ${maxFiles}`
      );
      return false;
    }

    const fileExtension = '.' + file.name.split('.').pop()?.toLowerCase();
    const isValidType = acceptedTypes.some(
      (type) => type.toLowerCase() === fileExtension
    );

    if (!isValidType) {
      message.error(
        `${intl.formatMessage({ id: 'file_uploader.invalid_type' })} ${acceptedTypes.join(', ')}`
      );
      return false;
    }

    const fileSizeInMB = file.size / 1024 / 1024;
    if (fileSizeInMB > maxSize) {
      message.error(
        `${intl.formatMessage({ id: 'file_uploader.file_too_large' })} ${maxSize}MB`
      );
      return false;
    }

    return true;
  };

  const beforeUpload = (file: File) => {
    const fileName = file.name.toLowerCase();

    const existingFileNames = new Set<string>([
      ...existingFilePaths.map((path) => {
        const name = path.split('/').pop() || '';
        return name.toLowerCase();
      }),
      ...(fileList
        .map((f) => f.name?.toLowerCase())
        .filter(Boolean) as string[]),
    ]);

    if (existingFileNames.has(fileName)) {
      message.error(intl.formatMessage({ id: 'file_uploader.duplicate_file' }));
      return Upload.LIST_IGNORE;
    }

    if (!validateFile(file)) {
      return Upload.LIST_IGNORE;
    }

    return false;
  };

  const handleChange: UploadProps['onChange'] = ({ fileList: newFileList }) => {
    const existingFileUids = new Set<string>(
      fileList.map((f) => f.uid).filter(Boolean) as string[]
    );

    const existingFileNames = new Set<string>([
      ...existingFilePaths.map((path) => {
        const name = path.split('/').pop() || '';
        return name.toLowerCase();
      }),
      ...(fileList
        .map((f) => f.name?.toLowerCase())
        .filter(Boolean) as string[]),
    ]);

    const preservedFiles = newFileList.filter((file) =>
      existingFileUids.has(file.uid)
    );

    const newFiles = newFileList.filter(
      (file) => !existingFileUids.has(file.uid) && file.originFileObj
    );

    const seenInBatch = new Set<string>();
    const validNewFiles = newFiles
      .filter((file) => {
        if (file.originFileObj) {
          const fileName = (file.name || file.originFileObj.name).toLowerCase();

          if (existingFileNames.has(fileName)) {
            message.error(
              intl.formatMessage({ id: 'file_uploader.duplicate_file' })
            );
            return false;
          }

          if (seenInBatch.has(fileName)) {
            message.error(
              intl.formatMessage({ id: 'file_uploader.duplicate_file' })
            );
            return false;
          }

          seenInBatch.add(fileName);
        }
        return true;
      })
      .map((file): ExtendedUploadFile => {
        if (file.originFileObj) {
          const fileName = file.name || file.originFileObj.name;
          const fileFormat = fileName.includes('.')
            ? fileName.split('.').pop()?.toUpperCase()
            : undefined;
          return {
            ...file,
            file_size: file.originFileObj.size,
            file_format: fileFormat,
            created_at: new Date().toISOString(),
          } as ExtendedUploadFile;
        }
        return file as ExtendedUploadFile;
      });

    const validFiles = [...preservedFiles, ...validNewFiles];
    const totalFiles = existingFilePaths.length + validFiles.length;

    if (maxFiles && totalFiles > maxFiles) {
      message.warning(
        `${intl.formatMessage({ id: 'file_uploader.max_files' })} ${maxFiles}`
      );
      const remainingSlots = maxFiles - existingFilePaths.length;
      const limitedFiles = validFiles.slice(0, Math.max(0, remainingSlots));
      onFileListChange(limitedFiles);
    } else {
      onFileListChange(validFiles);
    }
  };

  const handleRemove = (file: UploadFile) => {
    const newFileList = fileList.filter((item) => item.uid !== file.uid);
    onFileListChange(newFileList);
  };

  const uploadProps: UploadProps = {
    name,
    fileList: allFiles,
    beforeUpload,
    onChange: handleChange,
    onRemove: handleRemove,
    multiple,
    disabled: effectiveDisabled,
    showUploadList: false,
    listType: 'picture-card',
  };

  return (
    <StyledFileUploader>
      {label && <Label className="file-uploader-label" label={label} />}
      <Upload.Dragger {...uploadProps}>
        <div className="ant-upload-drag-icon">
          <FileOutlined />
        </div>
        <Label
          label={`${intl.formatMessage({ id: 'file_uploader.upload_files' })} (${acceptedTypes.join(', ')})`}
          center={true}
        />
        <Label
          label={`${intl.formatMessage({ id: 'file_uploader.max_size' })}: ${maxSize}MB`}
          center={true}
          regular={true}
        />
        {maxFiles > 0 && (
          <Label
            label={`${allFiles.length}/${maxFiles} ${intl.formatMessage({ id: 'file_uploader.files' })}`}
            center={true}
            regular={true}
            color={isMaxFilesReached ? 'theme-color' : undefined}
            bold={isMaxFilesReached}
          />
        )}
      </Upload.Dragger>
      {allFiles.length > 0 && (
        <CustomUploadList
          fileList={allFiles}
          onRemove={handleRemove}
          onDownload={onDownload}
          onDelete={onDelete}
          showActions={true}
        />
      )}
    </StyledFileUploader>
  );
};
